import React, { ReactNode, use, useEffect } from 'react';
import { LoginDto, User } from './types';
import { UserApiService } from './UserApiService';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { AsyncAction } from 'shared/types';
import { Axios, AxiosError } from 'axios';

declare global {
   interface Window {
      dataLayer?: any[];
   }
}

const UserContext = React.createContext<{
   user: User | null;
   isLoggedIn: boolean;
   // login: AsyncAction<LoginDto>;
   setContext: AsyncAction<User, unknown>;
   logout: () => void;
   onSetUser: (user: User) => void;
   loading: boolean;
   error: string | null;
} | null>(null);

const UserToken = 'userToken' as const;

export const UserContextProvider = ({ children }: { children: ReactNode }) => {
   const { t } = useTranslation('global');

   const [user, setUser] = React.useState<User | null>(null);
   const [loading, setLoading] = React.useState(false);
   const [error, setError] = React.useState<string | null>(null);

   const setContext: AsyncAction<User> = async (user: User, onSucess, onError) => {
      setLoading(true);

      try {
         const resp = await UserApiService.setContext({
            user_id: user.user_id,
            user_type: user.user_type,
            lang: 'uk',
         });

         if (resp.status === 200) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
               event: 'login',
               user_id: user.user_id,
               authentication_method: 'email',
            });

            Cookies.set('lname', user.last_name);
            Cookies.set('user_id', user.user_id?.toString());
            Cookies.set('fname', user.first_name);
            Cookies.set('tokenClient', user.token);
            Cookies.set('user_type', user.user_type);
            Cookies.set('contactNumber', user.contact || '');
            Cookies.set('emailAddress', user.email_address);

            if (user.employer_id) {
               Cookies.set('employer_id', user.employer_id.toString());
            }
            if (user.company_name) {
               Cookies.set('fname', user.company_name);
            }
            if (user.role) {
               Cookies.set('employer_role', user.role);
            }

            /**
             * TODO accessToken auth token when refactor
             */
            Cookies.set('accessToken', user.token);
            toast.success(t('jobseekerLogin.welcome') + ', ' + user.first_name);

            setUser(user);
            onSucess?.();
         }
      } catch (e) {
         console.warn('UserContext: setContext Error', e);
         onError?.(e);
      } finally {
         setLoading(false);
      }
   };

   const onSetUser = (user: User) => {
      setUser(user);
   };

   const logout = () => {
      Cookies.remove('accessToken');
      setUser(null);
   };

   return (
      <UserContext.Provider
         value={{
            user,
            isLoggedIn: !!user,
            loading,
            error,
            // login,
            setContext,
            logout,
            onSetUser,
         }}
      >
         {children}
      </UserContext.Provider>
   );
};

export const useUserContext = () => {
   const ctx = React.useContext(UserContext);
   if (!ctx) {
      throw new Error('UserContext must be used inside FeatureJobsContextProvider');
   }
   return ctx;
};
